'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { IconChevronDown } from '@/components/icons/IconChevronDown';
import { stegaClean } from '@/lib/utils/stega';
import type { BusinessAreaDto, BusinessAreasNavItemDto } from '@/lib/sanity/dto';
import { cn } from '@/lib/utils/tw-merge';
import { businessAreaNavItem, resolveHref } from './navigation.utils';

type BusinessAreasMegaMenuProps = {
    item: BusinessAreasNavItemDto;
    areas: BusinessAreaDto[];
    isCurrent?: boolean;
};

/**
 * The Geschäftsbereiche menu: every area as a tile, four to a row. No preview
 * pane — a menu is a list you read to choose from, and footage that only ever
 * answered the pointer made the panel taller without making the choice easier.
 * All seven names and descriptions are on screen at once instead.
 *
 * Opening and closing are delayed by a beat so a cursor crossing the trigger on
 * its way elsewhere does not flash the panel.
 */
export function BusinessAreasMegaMenu({ item, areas, isCurrent = false }: BusinessAreasMegaMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        const onPointerDown = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) setIsOpen(false);
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, []);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    /**
     * The panel spans the header's content width, but it cannot say so in vw:
     * 100vw counts the scrollbar gutter while the header's padding resolves
     * against the document's client width, so a vw-based panel sits half a
     * scrollbar wide of the header's edges. Publish the width the header
     * actually sees; before hydration the CSS falls back to 100vw.
     */
    useEffect(() => {
        const sync = () => {
            document.documentElement.style.setProperty('--viewport-w', `${document.documentElement.clientWidth}px`);
        };

        sync();
        window.addEventListener('resize', sync);

        return () => window.removeEventListener('resize', sync);
    }, []);

    const scheduleOpen = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setIsOpen(true), 110);
    };

    const scheduleClose = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setIsOpen(false), 90);
    };

    /**
     * Choosing an area is the end of the menu's job. The pointer is still
     * inside the wrapper after the click, so nothing else would close the
     * panel — and a link to the page already showing navigates nowhere.
     */
    const closeNow = () => {
        clearTimeout(timerRef.current);
        setIsOpen(false);
    };

    if (!areas.length) return null;

    return (
        <div className="mega-menu" ref={wrapperRef} onMouseEnter={scheduleOpen} onMouseLeave={scheduleClose}>
            <button
                type="button"
                className="site-nav__trigger"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-current={isCurrent ? 'page' : undefined}
                onClick={() => {
                    clearTimeout(timerRef.current);
                    setIsOpen((open) => !open);
                }}
            >
                {item.label}
                <IconChevronDown className="site-nav__chevron" />
            </button>

            <div className={cn('mega-menu__panel', isOpen && 'is-open')} role="menu" aria-label={item.label}>
                <div className="mega-menu__list">
                    {areas.map((area) => (
                        <Link
                            key={area._id}
                            href={resolveHref(businessAreaNavItem(area))}
                            role="menuitem"
                            style={{ '--area': stegaClean(area.color) } as React.CSSProperties}
                            className="mega-menu__row"
                            onClick={closeNow}
                        >
                            <span className="mega-menu__name-row">
                                <span className="mega-menu__dot" aria-hidden="true" />
                                <span className="mega-menu__name">{area.title}</span>
                            </span>

                            <span className="mega-menu__meta">
                                <span className="mega-menu__line">{area.description}</span>
                            </span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
